import React, { use, useCallback, useEffect, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import Text from "../../components/Text";
import SignupFinishedImage from "../../assets/images/signup-finished-image.svg";
import { AuthManagerContext, IS_INITIAL_WORKER_ONBOARDING } from "../../contexts/AuthManager";
import ScreenCard from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { capitalizeEachFirstLetter } from "../../utils";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useTracking } from "../../hooks/tracking";
import { safeAsyncStorage } from "../../utils/safe-async-storage";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type ScreenParams = StackScreenProps<RootStackParamList>;

const SignupFinished = () => {
    const i18n = useI18n();
    const { colors, gridSize } = useTheme();
    const styles = useStyles();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const { user } = use(AuthManagerContext);
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { trackEvent } = useTracking();
    const next = useCallback(() => {
        navigation.reset({
            index: 0,
            routes: [{ name: "Home" }],
        });
    }, [navigation]);

    useEffect(() => {
        void trackEvent("post_signup_completed_worker");
    }, [trackEvent]);

    useFocusEffect(
        useCallback(() => {
            void safeAsyncStorage.setItem(IS_INITIAL_WORKER_ONBOARDING, "false");
        }, []),
    );

    return (
        <ScreenCard
            buttons={[{ title: i18n.t(`screens.auth.signupFinished.nextButton`), onPress: next }]}
            style={{ backgroundColor: colors.onPrimary, paddingTop: insets.top }}
            isContentContainerFlex
        >
            <VStack gap={1} h="2/3">
                <Text style={styles.title}>
                    {i18n.t(`screens.auth.signupFinished.title`, {
                        name: capitalizeEachFirstLetter(user?.firstName || ""),
                    })}
                </Text>

                <View style={{ marginHorizontal: -gridSize }}>
                    <SignupFinishedImage width="100%" style={{ overflow: "visible" }} />
                </View>
                <Text style={styles.subTitle}>
                    {i18n.t(`screens.auth.signupFinished.sub_title`)}
                </Text>
                <Text style={styles.description}>
                    {i18n.t(`screens.auth.signupFinished.description_${profileType}`)}
                </Text>
            </VStack>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                title: {
                    ...fonts.titleLarge,
                    textAlign: "center",
                },
                subTitle: {
                    ...fonts.titleMedium,
                    textAlign: "center",
                },
                description: {
                    ...fonts.bodyMedium,
                    textAlign: "center",
                },
            }),
        [fonts.bodyMedium, fonts.titleLarge, fonts.titleMedium],
    );
};

export default SignupFinished;
